/**
 * Canonical rarity / potential tier colours — shared by entity highlights,
 * equip chrome, battle boost aura, and choose-boost cards.
 *
 * Pair tiers use {@link sqColorPair} static stops. Mythic is a multi-stop
 * spectrum with {@link RARITY_TIER_MYTHIC_ACCENT_HEX} as the signature pink.
 */
import {
	type SqColorName,
	sqColorPair,
	sqColorStop,
} from "./sq-colors";

/** Core equipment / boost rarity ladder (excludes spotlight-only `new`). */
export type RarityTier =
	| "common"
	| "rare"
	| "epic"
	| "unique"
	| "legendary"
	| "mythic";

/**
 * Entity slot / list spotlight keys. `new` shares unique (amber) stops;
 * there is no `common` entity highlight.
 */
export type EntityItemHighlight =
	| "rare"
	| "epic"
	| "unique"
	| "legendary"
	| "new"
	| "mythic";

/** Documented out-of-palette mythic accent (also noted in `sq-colors.ts`). */
export const RARITY_TIER_MYTHIC_ACCENT_HEX = "#FF72E1";

/**
 * SQ family for solid fills / badge class gradients.
 * Mythic has no single family — use {@link rarityTierBorderBackground} /
 * {@link rarityTierAuraPalette} instead of a pair class.
 */
export const RARITY_TIER_SQ_COLOR: Record<RarityTier, SqColorName | null> = {
	common: "slate",
	rare: "blue",
	epic: "purple",
	unique: "amber",
	legendary: "green",
	mythic: null,
};

/** Map entity highlights onto the rarity ladder (`new` → unique). */
export function rarityTierFromEntityHighlight(
	highlight: EntityItemHighlight,
): RarityTier {
	if (highlight === "new") {
		return "unique";
	}
	return highlight;
}

export interface RarityTierAuraPalette {
	color: string;
	midColor: string;
	deepColor: string;
	/** Arc / stroke accent (hub meter, beams). */
	stroke: string;
}

function pairAura(color: SqColorName): RarityTierAuraPalette {
	const pair = sqColorPair(color, "static");
	const deep = sqColorPair(color, "deep");
	return {
		color: pair.from,
		midColor: pair.to,
		deepColor: deep.from,
		stroke: pair.from,
	};
}

export function rarityTierAuraPalette(tier: RarityTier): RarityTierAuraPalette {
	switch (tier) {
		case "common":
			return pairAura("slate");
		case "rare":
			return pairAura("blue");
		case "epic":
			return pairAura("purple");
		case "unique":
			return pairAura("amber");
		case "legendary":
			return pairAura("green");
		case "mythic":
			return {
				color: sqColorStop("purple", "static", "from"),
				midColor: RARITY_TIER_MYTHIC_ACCENT_HEX,
				deepColor: sqColorStop("red", "static", "from"),
				stroke: RARITY_TIER_MYTHIC_ACCENT_HEX,
			};
	}
}

function staticPairGradient(
	color: SqColorName,
	angle: "to bottom right" | "135deg" = "to bottom right",
): string {
	const pair = sqColorPair(color, "static");
	return `linear-gradient(${angle}, ${pair.from}, ${pair.to})`;
}

export function rarityTierMythicBorderBackground(): string {
	const green = sqColorStop("green", "static", "from");
	const purple = sqColorStop("purple", "static", "from");
	const red = sqColorStop("red", "static", "from");
	const orange = sqColorStop("orange", "static", "from");
	const amber = sqColorStop("amber", "static", "from");
	const blue = sqColorStop("blue", "static", "from");
	return `linear-gradient(135deg, ${green} 0%, ${blue} 14%, ${purple} 28%, ${RARITY_TIER_MYTHIC_ACCENT_HEX} 42%, ${red} 56%, ${orange} 70%, ${amber} 84%, ${green} 100%)`;
}

/**
 * Border / badge / flag surface background for a rarity tier.
 * Mythic returns the multi-stop spectrum; others use the SQ static pair.
 */
export function rarityTierBorderBackground(
	tier: RarityTier,
	angle: "to bottom right" | "135deg" = "to bottom right",
): string {
	if (tier === "mythic") {
		return rarityTierMythicBorderBackground();
	}
	const color = RARITY_TIER_SQ_COLOR[tier];
	if (color == null) {
		return staticPairGradient("slate", angle);
	}
	return staticPairGradient(color, angle);
}

/** Entity-highlight border background (preserves `new` amber 135deg). */
export function entityHighlightBorderBackground(
	highlight: EntityItemHighlight,
): string {
	if (highlight === "new") {
		return staticPairGradient("amber", "135deg");
	}
	return rarityTierBorderBackground(rarityTierFromEntityHighlight(highlight));
}

export function rarityTierBorderBeamColors(tier: RarityTier): {
	colorFrom: string;
	colorTo: string;
} {
	if (tier === "mythic") {
		return {
			colorFrom: sqColorStop("blue", "static", "from"),
			colorTo: sqColorStop("amber", "static", "from"),
		};
	}
	const color = RARITY_TIER_SQ_COLOR[tier];
	if (color == null) {
		const pair = sqColorPair("slate", "static");
		return { colorFrom: pair.from, colorTo: pair.to };
	}
	const pair = sqColorPair(color, "static");
	return { colorFrom: pair.from, colorTo: pair.to };
}

export function entityHighlightBorderBeamColors(
	highlight: EntityItemHighlight,
): { colorFrom: string; colorTo: string } {
	return rarityTierBorderBeamColors(rarityTierFromEntityHighlight(highlight));
}

/**
 * SQ color for class-based surfaces (`sqLinearGradientClass`), or null when
 * the caller must apply {@link rarityTierBorderBackground} as an inline style
 * (mythic rainbow).
 */
export function rarityTierSqColor(tier: RarityTier): SqColorName | null {
	return RARITY_TIER_SQ_COLOR[tier];
}
